import React from 'react';

const TroubleshootingGuide = () => {
  return (
    <div className="troubleshooting-guide">
      <h2>Troubleshooting Guide</h2>
      <p className="section-intro">
        Encountering issues with data imports or exports? This guide covers common problems and their solutions.
      </p>

      <div className="troubleshooting-section">
        <h3>Import Issues</h3>
        
        <div className="issue-card">
          <h4>File Upload Fails</h4>
          <div className="issue-content">
            <div className="possible-causes">
              <h5>Possible Causes:</h5>
              <ul>
                <li>File is too large (exceeds 10MB limit)</li>
                <li>File format is not supported</li>
                <li>File is corrupted</li>
                <li>Poor internet connection</li>
              </ul>
            </div>
            <div className="solutions">
              <h5>Solutions:</h5>
              <ol>
                <li>Ensure your file is under 10MB. Split larger files if necessary.</li>
                <li>Verify the file extension is .csv, .xlsx, .xls, or .json</li>
                <li>Try re-exporting the file from your launch monitor</li>
                <li>Check your internet connection and try again</li>
                <li>Try using a different browser</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="issue-card">
          <h4>Data Not Recognized Correctly</h4>
          <div className="issue-content">
            <div className="possible-causes">
              <h5>Possible Causes:</h5>
              <ul>
                <li>Column headers don't match expected format</li>
                <li>Data contains unexpected characters or formatting</li>
                <li>CSV delimiter is not a comma</li>
                <li>File encoding issues</li>
              </ul>
            </div>
            <div className="solutions">
              <h5>Solutions:</h5>
              <ol>
                <li>Use the column mapping tool during import to match your headers</li>
                <li>Check for special characters or formatting in your file</li>
                <li>Save your CSV with comma delimiters (not semicolons or tabs)</li>
                <li>Save your file with UTF-8 encoding</li>
                <li>Try opening and re-saving the file in a spreadsheet program</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="issue-card">
          <h4>Missing Data After Import</h4>
          <div className="issue-content">
            <div className="possible-causes">
              <h5>Possible Causes:</h5>
              <ul>
                <li>Some columns were not mapped correctly</li>
                <li>Data contained values outside acceptable ranges</li>
                <li>Shots were filtered out due to validation issues</li>
                <li>Empty cells in important columns</li>
              </ul>
            </div>
            <div className="solutions">
              <h5>Solutions:</h5>
              <ol>
                <li>Check the import summary for warnings about skipped data</li>
                <li>Review your column mapping to ensure all fields are correctly mapped</li>
                <li>Check the validation settings and adjust if necessary</li>
                <li>Look for empty cells in your source file and fill them if possible</li>
                <li>Try importing with the "Relaxed Validation" option</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="troubleshooting-section">
        <h3>Export Issues</h3>
        
        <div className="issue-card">
          <h4>Can't Find Export Option in Launch Monitor App</h4>
          <div className="issue-content">
            <div className="possible-causes">
              <h5>Possible Causes:</h5>
              <ul>
                <li>Using an outdated app version</li>
                <li>Export feature requires premium subscription</li>
                <li>Export option is hidden in a submenu</li>
              </ul>
            </div>
            <div className="solutions">
              <h5>Solutions:</h5>
              <ol>
                <li>Update your launch monitor app to the latest version</li>
                <li>Check if your subscription level includes data export features</li>
                <li>Look for export options in the session details view</li>
                <li>Check for "Share" or "..." menu options that might contain export functionality</li>
                <li>Consult the manufacturer's documentation for specific export instructions</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="issue-card">
          <h4>Exported File Has Unexpected Format</h4>
          <div className="issue-content">
            <div className="possible-causes">
              <h5>Possible Causes:</h5>
              <ul>
                <li>Launch monitor exports in proprietary format</li>
                <li>Export settings not configured correctly</li>
                <li>Regional settings affecting number formats</li>
              </ul>
            </div>
            <div className="solutions">
              <h5>Solutions:</h5>
              <ol>
                <li>Look for specific CSV or Excel export options</li>
                <li>Check export settings for format options</li>
                <li>Try opening the file in a spreadsheet program and re-saving in standard CSV format</li>
                <li>Ensure your device's regional settings use periods for decimal points</li>
                <li>Contact the launch monitor manufacturer for export format documentation</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="troubleshooting-section">
        <h3>Data Quality Issues</h3>
        
        <div className="issue-card">
          <h4>Unrealistic Distance Values</h4>
          <div className="issue-content">
            <div className="possible-causes">
              <h5>Possible Causes:</h5>
              <ul>
                <li>Launch monitor calibration issues</li>
                <li>Incorrect unit settings (yards vs. meters)</li>
                <li>Ball or club not detected properly</li>
                <li>Environmental settings not configured correctly</li>
              </ul>
            </div>
            <div className="solutions">
              <h5>Solutions:</h5>
              <ol>
                <li>Recalibrate your launch monitor according to manufacturer instructions</li>
                <li>Check unit settings in both your launch monitor and RangeDat</li>
                <li>Ensure proper lighting and positioning for optimal ball tracking</li>
                <li>Verify environmental settings (altitude, temperature, etc.) are correct</li>
                <li>Use the data filtering tools in RangeDat to remove outliers</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="issue-card">
          <h4>Missing Club Data</h4>
          <div className="issue-content">
            <div className="possible-causes">
              <h5>Possible Causes:</h5>
              <ul>
                <li>Club not selected in launch monitor before shots</li>
                <li>Club column not included in export</li>
                <li>Club data not mapped correctly during import</li>
              </ul>
            </div>
            <div className="solutions">
              <h5>Solutions:</h5>
              <ol>
                <li>Always select the club in your launch monitor app before taking shots</li>
                <li>Ensure club information is included in your export settings</li>
                <li>Use the column mapping tool to correctly map club data</li>
                <li>Edit session details in RangeDat to add missing club information</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="issue-card">
          <h4>Inconsistent Data Between Sessions</h4>
          <div className="issue-content">
            <div className="possible-causes">
              <h5>Possible Causes:</h5>
              <ul>
                <li>Different launch monitors used</li>
                <li>Varying environmental conditions</li>
                <li>Different ball types</li>
                <li>Inconsistent setup or positioning</li>
              </ul>
            </div>
            <div className="solutions">
              <h5>Solutions:</h5>
              <ol>
                <li>Note the launch monitor used for each session in session details</li>
                <li>Record environmental conditions (indoor/outdoor, temperature, etc.)</li>
                <li>Use consistent ball types for comparable data</li>
                <li>Follow the same setup procedure for each session</li>
                <li>Use RangeDat's normalization features to adjust for different conditions</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="troubleshooting-section">
        <h3>Account and Access Issues</h3>
        
        <div className="issue-card">
          <h4>Can't Access Launch Monitor Cloud Data</h4>
          <div className="issue-content">
            <div className="possible-causes">
              <h5>Possible Causes:</h5>
              <ul>
                <li>Account credentials expired</li>
                <li>API access requires premium subscription</li>
                <li>Cloud service temporarily unavailable</li>
              </ul>
            </div>
            <div className="solutions">
              <h5>Solutions:</h5>
              <ol>
                <li>Log out and log back in to your launch monitor account</li>
                <li>Check your subscription status and upgrade if necessary</li>
                <li>Try again later if the service might be experiencing downtime</li>
                <li>Contact the launch monitor customer support</li>
                <li>Use manual export and import as an alternative</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="contact-support">
        <h3>Still Having Issues?</h3>
        <p>
          If you're still experiencing problems after trying these solutions, our support team is here to help.
        </p>
        <div className="support-options">
          <div className="support-option">
            <h4>Email Support</h4>
            <p>Send us your issue details and files for personalized assistance.</p>
            <a href="mailto:support@rangedat.com" className="support-link">support@rangedat.com</a>
          </div>
          <div className="support-option">
            <h4>Live Chat</h4>
            <p>Chat with our support team during business hours.</p>
            <a href="#" className="support-link" onClick={(e) => { e.preventDefault(); alert('Live chat would open here'); }}>Start Chat</a>
          </div>
          <div className="support-option">
            <h4>Community Forum</h4>
            <p>Ask questions and get help from other RangeDat users.</p>
            <a href="/community" className="support-link">Visit Forum</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TroubleshootingGuide;
